import {EventEmitter, Injectable} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {ActivatedRoute} from '@angular/router';
import {CookieService} from 'ngx-cookie-service';
import {TranslateService} from '@ngx-translate/core';
import {
  catchError,
  concatMap,
  EMPTY,
  forkJoin,
  from,
  map,
  noop,
  Observable,
  of,
  throwError,
  timer
} from 'rxjs';
import {filter, switchMap, tap} from 'rxjs/operators';
import {cloneDeep, extend, find, forEach, get, truncate} from 'lodash';
/** Components */
import {
  ATTACHMENTS_ACTIONS,
  AttachmentType,
  ChangesType,
  ConfigService,
  DialogHeight,
  DialogWidth,
  handleError,
  ImageAttachment,
  IMPACT_PRIORITY,
  IntercomService,
  OrgModelLinkNode,
  PermissionsService,
  Q9HtmlEncoderPipe,
  RefModelLinkNode,
  Requirement,
  RequirementLink,
  RequirementReference,
  SessionService,
  SnackBarService,
  Story,
  StoryLink,
  StoryReference,
  TakeUntilComponentDestroy,
  ThemePalette
} from '@q9elements/ui-kit/common';
import {
  AttachDataTableDialogComponent,
  AttachmentDialogComponent,
  AttachmentDialogPayload,
  AttachUrlDialogComponent,
  DataTableAttachmentDialogData,
  DataTableAttachmentSelection,
  DataTableRecordsComponent,
  DataTableRecordsDialogData,
  ExcludeObject,
  ImageAttachDialogComponent,
  ImageAttachmentData,
  NoteAttachDialogComponent,
  UrlAttachmentsDialogActions,
  UrlAttachmentsDialogOnSubmitPayload
} from '@q9elements/ui-kit/attachments';
import {
  AddStoryDialogComponent,
  AttachStoryDialogComponent
} from '@q9elements/ui-kit/changes/stories';
import {AttachChangeParams, CustomFieldsFormDaoService} from '@q9elements/ui-kit/changes/shared';
import {RequirementLinkFields, StoryLinkFields} from '@q9elements/ui-kit/refmodels';
import {ProposedNodeAttachDialogComponent} from '@q9elements/ui-kit/components';
import {
  AddRequirementDialogComponent,
  AttachRequirementDialogComponent,
  Q9RequirementsDaoService
} from '@q9elements/ui-kit/changes/requirements';
import {NavigationService} from '../../core/navigation/navigation.service';
import {DiagramJsonHandlerService, IActJson} from '../../diagram-data/diagram-json-handler.service';
import {ActivityCommands} from '../../commands/activity/activity.commands';
import {UndoService} from '../../commands/undo.service';
import {ModeDataService} from '../../editor-state/mode-data.service';
import {AuthService} from '../../core/auth/auth.service';
import {PopupManagerService} from '../../core/services/popup-manager.service';
import {ClientEnvService} from '../../core/services/client-env.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../editor-interface/shared/models/diagram-version.constant';
import {RecentlySeenService} from '../../shared/service/recently-seen.service';
import {AddOrgModelNodeDialogComponent} from './dialogs/search-org-model-dialog/add-org-model-node-dialog.component';
import {OrgModelNodesAttachmentDialogData} from './dialogs/search-org-model-dialog/data-access/models/org-model-nodes-attachment-dialog-data.interface';
import {OrgModelNodesAttachment} from 'src/app/shared/models/org-model-nodes-attachment.interface';
import {ReorderEvent} from 'src/app/shared/models/reorder-event.interface';
import {shouldSkipUndoAttachment} from 'src/app/util/should-skip-undo-attachment';
import {MxCell} from '../../editor-interface/shared/models/mx-cell.interface';
import {AttachOrgNodeLinkDialogComponent} from './dialogs/attach-org-node-link/attach-org-node-link-dialog.component';
import {AttachOrgNodeLinkDialogResult} from './dialogs/attach-org-node-link/models/org-node-link.interface';
import {Activity} from '../../shared/models/activity.interface';
import {LinkedSFNodesService} from '../services/linked-sf-nodes.service';
import {formatLinkedNode} from './dialogs/search-org-model-dialog/utils/formatLinkedNode';

const REF_MODEL_KEY = 'ELEMENTS.REF_MODEL.COPIED_NODE';
const ORG_MODEL_KEY = 'ELEMENTS.ORG_MODEL.COPIED_NODE';
const REQUIREMENT_KEY = 'ELEMENTS.COPIED.REQUIREMENT';
const STORY_KEY = 'ELEMENTS.COPIED.STORY';

@Injectable()
export class AttachmentsService extends TakeUntilComponentDestroy {
  constructor(
    public popupSvc: PopupManagerService,
    private authSvc: AuthService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private modeDataSvc: ModeDataService,
    private navSvc: NavigationService,
    private activityCommands: ActivityCommands,
    private linkedSfNodesService: LinkedSFNodesService,
    private undoSvc: UndoService,
    private route: ActivatedRoute,
    private cookieSvc: CookieService,
    private snackBarService: SnackBarService,
    private translateSvc: TranslateService,
    private clientEnvSvc: ClientEnvService,
    private encodeHtmlPipe: Q9HtmlEncoderPipe,
    private readonly recentlySeenSvc: RecentlySeenService,
    private readonly customFieldsDaoService: CustomFieldsFormDaoService,
    private readonly requirementsDaoService: Q9RequirementsDaoService,
    private readonly sessionService: SessionService,
    private permissionsService: PermissionsService,
    private intercomService: IntercomService
  ) {
    super();
  }

  attachNote(cell: any, attachment?): void {
    const cellJson = cell.getJson();

    if (attachment) {
      attachment.cellId = cell.id;
    }

    const onSubmit = (action: ATTACHMENTS_ACTIONS, attachmentData, lastValue) => {
      attachmentData.itemMxKey = Number(cell.id);

      if (action === ATTACHMENTS_ACTIONS.UPDATE || action === ATTACHMENTS_ACTIONS.REMOVE) {
        const processingAttachment = cellJson.attachments.find(att => att.value === lastValue);

        if (processingAttachment) {
          attachmentData.mxKey = processingAttachment.mxKey;
        }
      }

      this.processNoteAttachment(attachmentData, action);

      return of(attachmentData);
    };

    this.popupSvc.open(NoteAttachDialogComponent, {
      disableClose: true,
      id: 'attach-note',
      width: DialogWidth.XL,
      panelClass: ['q9-dialog'],
      data: {
        attachment,
        params: {
          isPreview: !this.modeDataSvc.isEditMode()
        },
        onSubmit
      }
    });
  }

  processNoteAttachment(
    attachment,
    action: ATTACHMENTS_ACTIONS,
    emitter?: EventEmitter<any>
  ): void {
    let command;

    switch (action) {
      case ATTACHMENTS_ACTIONS.CREATE:
        command = this.activityCommands.addAttachment(attachment, emitter);
        this.undoSvc.executeCommand(command, true);
        break;

      case ATTACHMENTS_ACTIONS.UPDATE:
        command = this.activityCommands.changeAttachment(attachment, emitter);
        this.undoSvc.executeCommand(command, true);
        break;

      case ATTACHMENTS_ACTIONS.REMOVE:
        command = this.activityCommands.deleteAttachment(attachment);
        this.undoSvc.executeCommand(command, true);
        break;
    }
  }

  attachImage(cell: MxCell, attachment?: ImageAttachment): void {
    const onSubmit = (action: ATTACHMENTS_ACTIONS, attachmentData: ImageAttachmentData) => {
      return from(this.handleImageAttachment.call(this, action, attachmentData, cell.id)).pipe(
        handleError()
      );
    };

    this.popupSvc.open(ImageAttachDialogComponent, {
      width: DialogWidth.MD,
      panelClass: ['q9-dialog', 'image-attachment-dialog'],
      autoFocus: true,
      restoreFocus: true,
      data: {
        attachment,
        actionType: 'diagramAttachment',
        isPreview: !this.modeDataSvc.isEditMode(),
        onSubmit
      }
    });
  }

  private handleImageAttachment(
    action: string,
    attachmentData: ImageAttachmentData,
    cellId: number
  ) {
    attachmentData.itemMxKey = cellId;

    if (action === ATTACHMENTS_ACTIONS.CREATE) {
      const command = this.activityCommands.addAttachment(attachmentData);

      this.undoSvc.executeCommand(command, true);
    } else {
      attachmentData.mxKey = Number(attachmentData._id);
      const command = this.activityCommands.changeAttachment(attachmentData);

      this.undoSvc.executeCommand(command, true);
    }

    return of({
      success: true,
      action,
      attachmentData
    });
  }

  attachUrl(cell: any, attachment?): void {
    const onSubmit = (action, attachmentData: UrlAttachmentsDialogOnSubmitPayload) => {
      return from(this.handleUrlAttachment.call(this, action, attachmentData, cell)).pipe(
        handleError()
      );
    };

    this.popupSvc.open(AttachUrlDialogComponent, {
      disableClose: true,
      width: DialogWidth.LG,
      panelClass: ['q9-dialog'],
      data: {
        queryParams: {
          diagramId: this.diagramJsonSvc.get().id,
          activityMxKey: cell.id
        },
        attachment,
        onSubmit
      }
    });

    const attachmentId = attachment?.urlEntry?.id;

    if (attachmentId) {
      this.recentlySeenSvc.captureRecentlyViewedURL(attachmentId).subscribe(noop);
    }
  }

  private handleUrlAttachment(action: UrlAttachmentsDialogActions, attachmentData: any, cell: any) {
    if (!attachmentData) {
      return;
    }

    let command;

    switch (action) {
      case UrlAttachmentsDialogActions.CREATE_URL_ATTACHMENT:
        attachmentData.itemMxKey = cell.id;
        command = this.activityCommands.addAttachment(attachmentData);
        break;

      case UrlAttachmentsDialogActions.ATTACH_URL: {
        if (!attachmentData.records.length) {
          return;
        }

        const listData = {
          itemMxKey: cell.id,
          records: attachmentData.records.map((item, index) => {
            item.position = (cell.getJson()?.attachments || []).length + index;
            item.type = AttachmentType.URL_ENTRY;
            item.urlEntry = item.id;
            item.groups = attachmentData.groups;

            return item;
          })
        };

        command = this.activityCommands.addUrlRecord(listData);
        break;
      }

      case UrlAttachmentsDialogActions.SAVE_TO_LIBRARY:
        attachmentData.activityMxKey = cell.id;

        if (!attachmentData.mxKey) {
          attachmentData.mxKey = attachmentData._id;
        }

        command = this.activityCommands.convertAttachment(attachmentData);
        break;

      case UrlAttachmentsDialogActions.UPDATE_ATTACHMENT:
        attachmentData.itemMxKey = cell.id;
        attachmentData.mxKey = Number(attachmentData._id);
        command = this.activityCommands.changeAttachment(attachmentData);
        break;
    }

    return from(this.undoSvc.executeCommand(command, false)).pipe(
      map(res => ({
        success: true,
        attachmentData: res
      })),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  attachRefNodeLink(cell: MxCell, attachment?: Partial<RefModelLinkNode>): void {
    const action = {
      type: attachment ? ATTACHMENTS_ACTIONS.UPDATE : ATTACHMENTS_ACTIONS.CREATE,
      title: attachment ? 'CORE.GENERAL.SAVE' : 'CORE.GENERAL.ATTACH'
    };
    const preparedAttachment = cloneDeep(attachment) || {
      ...this.getAttachmentFromCookies(REF_MODEL_KEY),
      type: 'refModelNode'
    };

    const onSubmit = (dialogData: AttachmentDialogPayload, _: string) => {
      const refNodeLinkAttachment = {
        ...this.prepareRefNodeLinkAttachmentData(action.type, preparedAttachment, dialogData),
        itemMxKey: cell.id
      };

      return from(
        this.handleNodeLinksAttachment.call(this, refNodeLinkAttachment, action.type)
      ).pipe(handleError());
    };

    const title = this.getNodeLinksAttachmentTitle(preparedAttachment, action.type);

    const data = {
      showTitle: true,
      isPreview: !this.modeDataSvc.isEditMode(),
      action,
      title,
      type: preparedAttachment.type,
      attachment: preparedAttachment,
      entityId: this.diagramJsonSvc.get().diagramId,
      onSubmit
    };

    this.popupSvc.open(AttachmentDialogComponent, {
      disableClose: true,
      width: DialogWidth.MD,
      panelClass: ['q9-dialog', 'rmn-attachment-dialog'],
      data: {
        color: ThemePalette.PRIMARY,
        ...data
      }
    });
  }

  attachOrgNodeLink(cell: MxCell, attachment?: Partial<OrgModelLinkNode>): void {
    const action = {
      type: attachment ? ATTACHMENTS_ACTIONS.UPDATE : ATTACHMENTS_ACTIONS.CREATE,
      title: attachment ? 'CORE.GENERAL.SAVE' : 'CORE.GENERAL.ATTACH'
    };

    if (attachment) {
      attachment.name = attachment.title;
    }

    const preparedAttachment =
      cloneDeep(attachment) || this.getAttachmentFromCookies(ORG_MODEL_KEY);

    const onSubmit = (dialogData: AttachOrgNodeLinkDialogResult) => {
      const orgNodeLinkAttachment = {
        ...dialogData,
        itemMxKey: cell.id
      };

      return from(
        this.handleNodeLinksAttachment.call(this, orgNodeLinkAttachment, action.type)
      ).pipe(handleError());
    };

    const title = this.getNodeLinksAttachmentTitle(preparedAttachment, action.type, true);
    const data = {
      isEdit: !!attachment,
      action,
      title,
      attachment: preparedAttachment,
      onSubmit
    };

    this.popupSvc.open(AttachOrgNodeLinkDialogComponent, {
      disableClose: true,
      width: DialogWidth.MD,
      panelClass: ['q9-dialog', 'rmn-attachment-dialog'],
      data
    });
  }

  private handleNodeLinksAttachment(data: unknown, actionType: ATTACHMENTS_ACTIONS) {
    const command =
      actionType === ATTACHMENTS_ACTIONS.CREATE
        ? this.activityCommands.addAttachment(data)
        : this.activityCommands.changeAttachment(data);

    this.undoSvc.executeCommand(command);

    return of({
      success: true,
      actionType,
      data
    });
  }

  public attachOrgModelNodes(cell: any): void {
    const {mxKey} = cell.getJson();

    const data: OrgModelNodesAttachmentDialogData = {
      activityMxKey: mxKey
    };

    const dialogRef = this.popupSvc.open(AddOrgModelNodeDialogComponent, {
      disableClose: true,
      id: 'attach-rmn',
      width: DialogWidth.LG_XL,
      height: DialogHeight.LG,
      data
    });

    dialogRef
      .afterClosed()
      .subscribe((orgModelNodesAttachments: OrgModelNodesAttachment[]) =>
        this.runOrgModelNodesAttachmentCommand(orgModelNodesAttachments)
      );
  }

  public attachDatatable(cell: any): void {
    const dialogData = this.getDataTableAttachmentDialogData(cell);

    this.openAttachDataTableDialog(dialogData)
      .afterClosed()
      .pipe(
        filter(attachment => !!attachment),
        tap(attachment => {
          const dialogRef = this.openDataTableRecordsDialog(cell, attachment);

          this.handleRecordCount(dialogRef, cell, attachment);
        }),
        catchError(() => {
          this.snackBarService.error('CORE.DATA_TABLE_ATTACHMENTS.NO_DATA_STR');

          return EMPTY;
        })
      )
      .subscribe();
  }

  private getDataTableAttachmentDialogData(cell: MxCell): DataTableAttachmentDialogData {
    return {
      isPreview: !this.modeDataSvc.isEditMode(),
      anchorTarget: this.clientEnvSvc.anchorTarget,
      excludeObject: this.getExcludeObject(cell),
      onSubmit: (data: DataTableAttachmentSelection): Observable<unknown> => {
        return this.handleDataTableAttachment.call(this, cell, data);
      }
    };
  }

  private getRecordsDialogData(cell: MxCell, attachment: any): DataTableRecordsDialogData {
    const {title, groups, viewersCanAddRecords} = attachment;

    return {
      title,
      groups,
      viewersCanAddRecords: !!viewersCanAddRecords,
      userCanAddRecords: this.userCanAddRecords(attachment),
      isPreview: !this.modeDataSvc.isEditMode(),
      urlConfig: {
        diagramId: this.diagramJsonSvc.get().diagramId,
        diagramState: this.navSvc.getCurrent().state,
        itemMxKey: cell.id,
        mxKey: attachment.mxKey
      }
    };
  }

  private getExcludeObject(cell: any): ExcludeObject {
    return {
      activity: cell.getJson().mxKey,
      diagramId: this.diagramJsonSvc.get().diagramId
    };
  }

  private openAttachDataTableDialog(
    dialogData: DataTableAttachmentDialogData
  ): MatDialogRef<AttachDataTableDialogComponent> {
    return this.popupSvc.open(AttachDataTableDialogComponent, {
      autoFocus: false,
      width: DialogWidth.MD,
      panelClass: ['q9-dialog', 'data-table-attachment-dialog'],
      data: dialogData
    });
  }

  private handleDataTableAttachment(
    cell: MxCell,
    data: DataTableAttachmentSelection
  ): Observable<unknown> {
    const {title, type, dataStructureDefinition, groups} = data;
    const param = {
      title,
      dataStructureDefinition,
      groups,
      type,
      itemMxKey: cell.id
    };
    const command = this.activityCommands.addAttachment(param);

    return from(this.undoSvc.executeCommand(command, true)).pipe(
      tap(attachment => this.updateRecordsCount(attachment))
    );
  }

  private handleRecordCount(
    dialogRef: MatDialogRef<DataTableRecordsComponent>,
    cell: MxCell,
    attachment: any
  ): void {
    dialogRef.componentInstance.recordCount
      .pipe(
        tap((records: number) => {
          this.updateRecordsCount({
            itemMxKey: cell.id,
            mxKey: attachment.mxKey,
            dtRecordsCount: records
          });
        })
      )
      .subscribe();
  }

  attachRequirementLink(cell: MxCell, attachment?: Requirement): void {
    const preparedAttachment = attachment
      ? cloneDeep(attachment)
      : this.getRequirementDataFromCache();

    const onSubmit = (payload: RequirementLink) => {
      return from(
        this.handleAttachLink.call(this, payload, cell.id, AttachmentType.REQUIREMENT, attachment)
      ).pipe(handleError());
    };

    const reqId = attachment ? preparedAttachment.requirement.reqId : preparedAttachment.reqId;

    const data: AttachChangeParams = {
      isPreview: !this.modeDataSvc.isEditMode(),
      isEdit: !!attachment,
      entity: {
        ...preparedAttachment,
        externalUrl: `${ConfigService.environment.MAIN_APP_URL}/t/${this.authSvc.getTeamIndex()}/requirements?id=${reqId}`
      } as RequirementReference,
      title: attachment
        ? 'CORE.ATTACHMENTS.EDIT_REQUIREMENT'
        : 'CORE.ATTACHMENTS.ATTACH_REQUIREMENT',
      formConfig: [
        RequirementLinkFields.REQ_ID,
        RequirementLinkFields.REQUIREMENT,
        RequirementLinkFields.REQ_REASON,
        RequirementLinkFields.REQ_DETAILS
      ],
      onSubmit
    };

    this.popupSvc.open(AttachRequirementDialogComponent, {
      width: DialogWidth.MD,
      panelClass: ['q9-dialog'],
      data: {
        color: ThemePalette.PRIMARY,
        ...data
      }
    });
  }

  attachStoryLink(cell: MxCell, attachment?: Story): void {
    const preparedAttachment = attachment ? cloneDeep(attachment) : this.getStoryDataFromCache();

    const onSubmit = (payload: StoryLink) => {
      return from(
        this.handleAttachLink.call(this, payload, cell.id, AttachmentType.STORY, attachment)
      ).pipe(handleError());
    };

    const storyId = attachment ? preparedAttachment.story.storyId : preparedAttachment.storyId;

    const data: AttachChangeParams = {
      isPreview: !this.modeDataSvc.isEditMode(),
      isEdit: !!attachment,
      entity: {
        ...preparedAttachment,
        externalUrl: `${ConfigService.environment.MAIN_APP_URL}/t/${this.authSvc.getTeamIndex()}/stories?id=${storyId}`
      } as StoryReference,
      title: attachment ? 'CORE.ATTACHMENTS.EDIT_STORY' : 'CORE.ATTACHMENTS.ATTACH_STORY',
      formConfig: [StoryLinkFields.STORY, StoryLinkFields.STORY_ID, StoryLinkFields.IMPACT_STATUS],
      onSubmit
    };

    this.popupSvc.open(AttachStoryDialogComponent, {
      width: DialogWidth.MD,
      panelClass: ['q9-dialog'],
      data: {
        color: ThemePalette.PRIMARY,
        ...data
      }
    });
  }

  private handleAttachLink(
    dialogData: RequirementLink | StoryLink,
    cellId: number,
    typeKey: AttachmentType,
    attachment?: RequirementReference | StoryReference
  ) {
    const data = attachment
      ? {
          ...dialogData,
          mxKey: attachment.mxKey,
          itemMxKey: cellId,
          [typeKey]: true
        }
      : {
          ...dialogData,
          itemMxKey: cellId
        };

    const command = attachment
      ? this.activityCommands.changeAttachment(data)
      : this.activityCommands.addAttachment(data);

    this.undoSvc.executeCommand(command);

    return of({
      success: true,
      data
    });
  }

  addRequirement(cell: MxCell): void {
    const requestParams = {
      diagramId: this.diagramJsonSvc.get().id,
      activityMxKey: Number(cell.id)
    };

    const onSubmit = requestParams => {
      return from(this.handleAddRequirementAttachment.call(this, requestParams, cell.id)).pipe(
        handleError()
      );
    };

    this.popupSvc.open(AddRequirementDialogComponent, {
      disableClose: true,
      width: DialogWidth.LG,
      panelClass: ['q9-dialog'],
      data: {
        groupsSelect: true,
        disabledCreate: !this.permissionsService.isRequirementsManager(),
        disabledPicklist: false,
        requestParams,
        onSubmit
      }
    });
  }

  private handleAddRequirementAttachment(data, cellId: number) {
    if (!data || !data.items) {
      return;
    }

    this.intercomService.fireEvent(
      'ACTION',
      this.intercomService.EVENTS_BY_AREA.CHANGES.DIAGRAM_REQUIREMENT_CREATED,
      {
        space_id: this.sessionService.teamId(),
        actor_user_id: this.sessionService.userId(),
        account_id: this.permissionsService.team()?.domain,
        event_date: new Date().toISOString(),
        product_area: this.intercomService.PRODUCT_AREAS.DIAGRAMS
      }
    );

    if (Array.isArray(data.items)) {
      forEach(data.items, function (row) {
        row.itemMxKey = cellId;
        row.groups = data.groups;
      });
      const command = this.activityCommands.addRequirements({
        items: data.items,
        itemMxKey: cellId
      });

      return this.undoSvc.executeCommand(command);
    }

    const requirement = data.requirementInfo;
    const link = extend({
      type: AttachmentType.REQUIREMENT,
      itemMxKey: cellId,
      reqId: requirement.reqId,
      requirement: requirement.id,
      title: requirement.summary.substring(0, 60),
      reqReason: requirement.reqReason,
      reqDetails: requirement.reqDetails,
      customFields: requirement.customFields,
      groups: requirement.groups,
      description: requirement.description
    });
    const command = this.activityCommands.addAttachment(link);

    this.undoSvc.executeCommand(command);

    return of({
      success: true,
      data
    });
  }

  generateBusinessRequirementsFrom(activityIDs: number[] = []): void {
    const diagramActivities: Activity[] = this.diagramJsonSvc.get().activities;
    const activitiesToProcess = activityIDs.length
      ? diagramActivities.filter(({mxKey}: Activity) => activityIDs.includes(mxKey))
      : diagramActivities;
    const validActivities = this.getBusinessRequirementValidActivities(activitiesToProcess);

    if (!validActivities.length) {
      return;
    }

    const groups: [string] = [this.diagramJsonSvc.groupTypeAll?.id];
    let generationStarted = false;

    this.popupSvc
      .openCreditWarningDialog({
        title: this.translateSvc.instant('EDITOR.GENERATE_BUSINESS_REQUIREMENTS'),
        confirmButtonText: this.translateSvc.instant('GPT_STORIES.GENERATE_PROCEED'),
        actionKey: 'GENERATE_STORY_REQUIREMENT_PER_ITEM_VIA_AI'
      })
      .pipe(
        filter(Boolean),
        switchMap(() =>
          this.customFieldsDaoService.getRequiredFieldsCount(ChangesType.REQUIREMENTS)
        ),
        switchMap((count: number) =>
          count ? this.popupSvc.openGenerateRequirementsDialog() : of({})
        ),
        filter(Boolean),
        switchMap(customFields => {
          this.snackBarService.open(this.translateSvc.instant('REQUIREMENTS_GENERATION.STARTED'));
          generationStarted = true;

          const processing$ = from(validActivities).pipe(
            concatMap((activity: Activity) =>
              this.generateBusinessRequirement(activity.text, customFields, {generated: true}).pipe(
                map(
                  (requirement: Requirement): Requirement => ({
                    ...requirement,
                    itemMxKey: activity.mxKey,
                    groups
                  })
                ),
                tap((requirement: Requirement): void =>
                  this.executeAddRequirement(activity, requirement)
                )
              )
            )
          );
          const showCompletedDelay$ = timer(1000);

          return forkJoin([processing$, showCompletedDelay$]);
        })
      )
      .subscribe({
        complete: () => {
          if (generationStarted) {
            this.snackBarService.success(
              this.translateSvc.instant('REQUIREMENTS_GENERATION.COMPLETED')
            );
          }

          this.intercomService.fireEvent(
            'ACTION',
            this.intercomService.EVENTS_BY_AREA.DIAGRAMS.DIAGRAM_TO_REQUIREMENTS_GENERATED,
            {
              space_id: this.sessionService.teamId(),
              actor_user_id: this.sessionService.userId(),
              account_id: this.permissionsService.team()?.domain,
              event_date: new Date().toISOString(),
              product_area: this.intercomService.PRODUCT_AREAS.DIAGRAMS
            }
          );
        }
      });
  }

  addStory(cell: MxCell): void {
    const requestParams = {
      diagramId: this.diagramJsonSvc.get().id,
      activityMxKey: Number(cell.id)
    };

    const onSubmit = requestParams => {
      return from(this.handleStoryAttachment.call(this, requestParams, cell.id)).pipe(
        handleError()
      );
    };

    this.popupSvc.open(AddStoryDialogComponent, {
      disableClose: true,
      width: DialogWidth.LG,
      panelClass: ['q9-dialog'],
      data: {
        groupsSelect: true,
        disabledCreate: !this.permissionsService.isRequirementsManager(),
        disabledPicklist: false,
        requestParams,
        onSubmit
      }
    });
  }

  private handleStoryAttachment(data, cellId: number) {
    if (!data || !data.items) {
      return;
    }

    let command;

    this.intercomService.fireEvent(
      'ACTION',
      this.intercomService.EVENTS_BY_AREA.CHANGES.DIAGRAM_STORY_CREATED,
      {
        space_id: this.sessionService.teamId(),
        actor_user_id: this.sessionService.userId(),
        account_id: this.permissionsService.team()?.domain,
        event_date: new Date().toISOString(),
        product_area: this.intercomService.PRODUCT_AREAS.DIAGRAMS
      }
    );

    if (Array.isArray(data.items)) {
      forEach(data.items, function (row) {
        row.itemMxKey = cellId;
        row.groups = data.groups;
      });
      command = this.activityCommands.addStories({
        items: data.items,
        itemMxKey: cellId
      });

      return this.undoSvc.executeCommand(command);
    }

    const story = data.storyInfo;
    const link = extend({
      type: AttachmentType.STORY,
      itemMxKey: cellId,
      storyId: story.storyId,
      story: story.id,
      title: story.summary.substring(0, 60),
      customFields: story.customFields,
      groups: story.groups,
      description: story.description
    });

    command = this.activityCommands.addAttachment(link);
    this.undoSvc.executeCommand(command);

    return of({
      success: true,
      data
    });
  }

  public attachProposedNode(cell): void {
    const {mxKey} = cell.getJson();

    const dialogRef = this.popupSvc.open(ProposedNodeAttachDialogComponent, {
      disableClose: true,
      panelClass: ['q9-dialog'],
      width: DialogWidth.LG_XL,
      height: DialogHeight.LG,
      data: {
        confirmText: 'CORE.UI_KIT.CREATE_AND_ADD_TO_ACTIVITY'
      }
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(({nodes}) => nodes?.length),
        switchMap(({nodes, groups, description}) =>
          forkJoin(
            nodes.map(node =>
              this.linkedSfNodesService
                .getLinkedSFNode(node.sfNode)
                .pipe(map(linkNode => formatLinkedNode(linkNode, groups, description, +mxKey)))
            )
          )
        )
      )
      .subscribe((orgModelNodesAttachments: OrgModelNodesAttachment[]) =>
        this.runOrgModelNodesAttachmentCommand(orgModelNodesAttachments)
      );
  }

  private runOrgModelNodesAttachmentCommand(orgModelNodesAttachments: OrgModelNodesAttachment[]) {
    if (!orgModelNodesAttachments?.length) {
      return;
    }

    const command = this.activityCommands.createOrgModelNodesAttachment(orgModelNodesAttachments);

    this.undoSvc.executeCommand(command).catch(_ => {
      const message = this.translateSvc.instant('SEARCH_ORG_NODE.ATTACH_ERROR_DEFAULT');

      this.snackBarService.error(message);
    });
  }

  private getBusinessRequirementValidActivities(activities: Activity[]): Activity[] {
    return activities
      .map(activity => ({
        ...activity,
        text: this.decode(activity.text)
          .replace(/<[^>]*>/g, '')
          .trim()
      }))
      .filter(({text}) => text.length >= 2);
  }

  public generateBusinessRequirement(
    summary: string,
    customFields: any,
    additional: {solution?: string; details?: string; generated?: boolean} = {}
  ): Observable<Requirement> {
    const normalizedSummary = summary.length > 255 ? truncate(summary, {length: 255}) : summary;

    const date = new Date();
    const payload: Partial<Requirement> = {
      summary: normalizedSummary,
      risks: IMPACT_PRIORITY.MEDIUM,
      priority: IMPACT_PRIORITY.MEDIUM,
      businessImpact: IMPACT_PRIORITY.MEDIUM,
      customFields,
      createdAt: date.toISOString(),
      timeZoneOffset: date.getTimezoneOffset()
    };

    if (additional.solution) {
      payload.solution = additional.solution;
    }

    if (additional.details) {
      payload.details = additional.details;
    }

    if (additional.generated) {
      payload.generated = additional.generated;
    }

    payload.creator = this.sessionService.user().id;

    return this.requirementsDaoService.createRequirement(payload);
  }

  public executeAddRequirement(activity: Activity, requirement: Requirement): void {
    const command = this.activityCommands.addRequirements({
      items: [requirement],
      itemMxKey: activity.mxKey
    });

    this.undoSvc.executeCommand(command);
  }

  private updateRecordsCount(attachment: any): void {
    const {itemMxKey, mxKey, dtRecordsCount = 0} = attachment;
    const parameters = {
      itemMxKey,
      mxKey,
      dtRecordsCount
    };

    this.diagramJsonSvc.update({changeAttachment: parameters});
  }

  private openDataTableRecordsDialog(
    cell: MxCell,
    attachment: any
  ): MatDialogRef<DataTableRecordsComponent> {
    const dialogData = this.getRecordsDialogData(cell, attachment);

    return this.popupSvc.open(DataTableRecordsComponent, {
      disableClose: true,
      width: DialogWidth.XL,
      panelClass: ['q9-dialog', 'data-table-attachment-dialog'],
      data: dialogData
    });
  }

  openAttachment(cell, attachment, type: AttachmentType) {
    if (!cell || !attachment) {
      return;
    }

    switch (type) {
      case AttachmentType.IMAGE:
        return this.attachImage(cell, attachment);

      case AttachmentType.TEXT:
        return this.attachNote(cell, attachment);

      case AttachmentType.URL:

      case AttachmentType.URL_ENTRY:
        return this.attachUrl(cell, attachment);

      case AttachmentType.REF_NODE:

      case AttachmentType.REF_MODEL:
        return this.attachRefNodeLink(cell, attachment);

      case AttachmentType.ORG_NODE:

      case AttachmentType.ORG_MODEL:
        return this.attachOrgNodeLink(cell, attachment);

      case AttachmentType.DATATABLE: {
        const dialogRef = this.openDataTableRecordsDialog(cell, attachment);

        this.handleRecordCount(dialogRef, cell, attachment);

        return;
      }

      case AttachmentType.REQUIREMENT:
        return this.attachRequirementLink(cell, attachment);

      case AttachmentType.STORY:
        return this.attachStoryLink(cell, attachment);
    }
  }

  get queryParams() {
    return this.route.snapshot.queryParams;
  }

  public reorderAttachment(
    {attachment, type, newIndex}: ReorderEvent,
    cell: any,
    oldPosition: number
  ): void {
    const {position, ...attachmentData} = attachment;
    const data = {
      itemMxKey: Number(cell.id),
      position: newIndex,
      oldPosition,
      requirement: type === AttachmentType.REQUIREMENT,
      story: type === AttachmentType.STORY,
      refModel: type === AttachmentType.REF_NODE,
      sfNode: type === AttachmentType.ORG_NODE,
      ...attachmentData
    };
    const command = this.activityCommands.changeAttachment(data);
    const skipUndo = shouldSkipUndoAttachment(attachment.type);

    this.undoSvc.executeCommand(command, skipUndo);
  }

  public deleteAttachment(e, cell) {
    if (!e || !cell) {
      return;
    }

    const commandData = e;

    const isUrlAttachment = e.type === AttachmentType.URL || e.type === AttachmentType.URL_ENTRY;
    const skipUndo = [AttachmentType.IMAGE, AttachmentType.DATATABLE, AttachmentType.TEXT].includes(
      e.type
    );

    commandData.itemMxKey = Number(cell.id);
    const command = this.activityCommands.deleteAttachment(commandData);

    if (isUrlAttachment) {
      from(this.undoSvc.executeCommand(command, false))
        .pipe(
          this.takeUntilComponentDestroy(),
          catchError(error => {
            const message = get(error, 'error.error', error);

            this.snackBarService.error(message);

            return throwError(() => error);
          })
        )
        .subscribe();
    } else {
      this.undoSvc.executeCommand(command, skipUndo);

      if (e.type === AttachmentType.DATATABLE) {
        this.undoSvc.clear();
      }
    }
  }

  public canAttachRefNode(jsonSource) {
    const modelJson = this.getAttachmentFromCookies(REF_MODEL_KEY);

    if (!modelJson) {
      return false;
    }

    const isPro = this.diagramJsonSvc.isPro();
    const isProTrial = this.diagramJsonSvc.isProTrial();
    const isSameTeam = this.diagramJsonSvc.checkSameTeam(modelJson.team);

    const isAttachedAlready = find(jsonSource.refModelAttachments, attachment => {
      if (!attachment.refModel || !attachment.refModelNode) {
        return false;
      }

      return (
        attachment.refModel === modelJson.refModel && attachment.refModelNode === modelJson._id
      );
    });

    return (isPro || isProTrial) && !isAttachedAlready && isSameTeam;
  }

  public canAttachOrgModelNodes(): boolean {
    return this.diagramJsonSvc.isPro() || this.diagramJsonSvc.isProTrial();
  }

  public canAttachCopiedOrgNode(source: IActJson): boolean {
    const orgModel = this.getAttachmentFromCookies(ORG_MODEL_KEY);

    if (!orgModel) {
      return false;
    }

    const isSameTeam = this.diagramJsonSvc.checkSameTeam(orgModel.team);
    const isAttachedAlready = source.orgModelAttachments?.some(
      (attachment: OrgModelLinkNode) => attachment.sfNode?._id === orgModel.sfNode
    );

    return this.canAttachOrgModelNodes() && !isAttachedAlready && isSameTeam;
  }

  public canAttachRequirement(jsonSource) {
    const requirementJson = this.getAttachmentFromCookies(REQUIREMENT_KEY);

    if (!requirementJson) {
      return false;
    }

    const isSameTeam = this.diagramJsonSvc.checkSameTeam(requirementJson.team);

    const isAttachedAlready = find(jsonSource.requirementAttachments, attachment => {
      if (!attachment.requirement) {
        return false;
      }

      const requirementId = attachment.requirement.id || attachment.requirement;

      return requirementId === requirementJson.requirement;
    });

    return isSameTeam && !isAttachedAlready;
  }

  public canAttachStory(jsonSource) {
    const storyJSON = this.getAttachmentFromCookies(STORY_KEY);

    if (!storyJSON) {
      return false;
    }

    const isSameTeam = this.diagramJsonSvc.checkSameTeam(storyJSON.team);

    const isAttachedAlready = find(jsonSource.storyAttachments, attachment => {
      if (!attachment.story) {
        return false;
      }

      const storyId = attachment.story.id || attachment.story;

      return storyId === storyJSON.story;
    });

    return isSameTeam && !isAttachedAlready;
  }

  private userCanAddRecords(attachment: any): boolean {
    const {viewersCanAddRecords} = attachment;
    const diagramState = this.navSvc.getCurrent().state;
    const mapState = this.diagramJsonSvc.get().map.state;
    const userCanAddRecords = this.authSvc.canEditDiagram() || viewersCanAddRecords;
    const isDraftOrMaster =
      diagramState === DIAGRAM_VERSION_STATES_ENUM.DRAFT ||
      diagramState === DIAGRAM_VERSION_STATES_ENUM.MASTER;
    const isNotDeleted = mapState !== 'deleted';

    return userCanAddRecords && isDraftOrMaster && isNotDeleted;
  }

  private prepareRefNodeLinkAttachmentData(
    actionType: ATTACHMENTS_ACTIONS,
    attachment: any,
    dialogData: AttachmentDialogPayload
  ): any {
    const {refModel, nodeDescription, _id, mxKey, refModelName} = attachment;

    return actionType === ATTACHMENTS_ACTIONS.CREATE
      ? {
          ...dialogData,
          type: AttachmentType.REF_MODEL,
          refModel,
          refModelNode: _id,
          refModelName: refModel.name ?? refModelName,
          nodeDescription
        }
      : {
          ...dialogData,
          mxKey,
          nodeDescription,
          refModel: true
        };
  }

  private getNodeLinksAttachmentTitle(
    attachment: Partial<OrgModelLinkNode | RefModelLinkNode>,
    actionType: ATTACHMENTS_ACTIONS,
    isOrg?: boolean
  ): string {
    if (!this.modeDataSvc.isEditMode()) {
      return isOrg ? (attachment as OrgModelLinkNode).name : attachment.title;
    }

    const titles = isOrg
      ? {
          [ATTACHMENTS_ACTIONS.CREATE]: 'CORE.ATTACHMENTS.ACT_ATTACH_ORG_MODEL',
          [ATTACHMENTS_ACTIONS.UPDATE]: 'CORE.ATTACHMENTS.ACT_EDIT_ORG_MODEL'
        }
      : {
          [ATTACHMENTS_ACTIONS.CREATE]: 'CORE.ATTACHMENTS.ATTACH_REFMODEL',
          [ATTACHMENTS_ACTIONS.UPDATE]: 'CORE.ATTACHMENTS.EDIT_REFMODEL'
        };

    return titles[actionType];
  }

  private getAttachmentFromCookies(key: string) {
    const nodeCache = this.cookieSvc.get(key);

    if (!nodeCache) {
      return null;
    }

    return JSON.parse(nodeCache);
  }

  private getRequirementDataFromCache() {
    const requirementCache = this.getAttachmentFromCookies(REQUIREMENT_KEY);
    const {name, reqId, requirement, reqReason, reqDetails, status} = requirementCache;
    const requirementTitle = name.substring(80, 0);

    return {
      title: this.decode(requirementTitle),
      description: '',
      requirement,
      reqId,
      reqReason: this.decode(reqReason),
      reqSummary: this.decode(name),
      reqDetails: this.cropText(reqDetails),
      status
    };
  }

  private getStoryDataFromCache() {
    const storyCache = this.getAttachmentFromCookies(STORY_KEY);
    const {story, description, name, storyId} = storyCache;
    const storyName = name.substring(80, 0);

    return {
      title: this.decode(storyName),
      description: '',
      story,
      storyDetails: this.decode(description),
      storyId,
      summary: this.decode(name)
    };
  }

  private cropText(text: string = ''): string {
    const decoded = this.decode(text);
    const maxLimit = decoded.length > 256 ? '...' : '';

    return decoded.substring(256, 0) + maxLimit;
  }

  private decode(string = '') {
    return this.encodeHtmlPipe.transform(string, true);
  }
}
